import { Accordion, SearchBox } from "@app/shared"
import { FC, JSX, useContext, useEffect, useMemo, useRef, useState } from "react";
import { EnterpriseComplianceFiltersContext } from "../contexts/EcdFiltersContext";
import { Link } from "react-router-dom";
import { SortDirections, Waffles, WafflesPaginationProps, wafflesKeyColumnName } from "@app/shared/waffles";
import { EnterpriseComplianceDashboardContext } from "../contexts/EcdContext";
import { SharedEcdDrillInTableData, sharedEcdDrillInColumnConfig, sharedEcdDrillInCustomSort, sharedEcdDrillInPaginationProps, sharedEcdDrillInSlicedTableData } from "../flyouts/EcdDrillInConfig";

interface EcdCountyByGapsAccordionProps {
  canonicalRegionSetId: number;
  countyName: string;
  countySSA: string;
}

interface TopCountiesFlyoutTableData {
  [wafflesKeyColumnName]: string;
  projectName: JSX.Element;
  compositeFillableGaps: string;
  unfillableGaps: string;
  overallGaps: string;
}

export const EcdCountyByGapsAccordion: FC<EcdCountyByGapsAccordionProps> = (props: EcdCountyByGapsAccordionProps) => {
  const filtersContext = useContext(EnterpriseComplianceFiltersContext);
  const ecdContext = useContext(EnterpriseComplianceDashboardContext);
  const [filterString, setFilterString] = useState("");
  const [data, setData] = useState<TopCountiesFlyoutTableData[]>([]);
  const take = useRef(100).current;
  const [skip, setSkip] = useState(0);

  useEffect(() => {
    setSkip(0);
  }, [filterString]);

  const onOpenAccordion = () => {
    if (data.length > 0) {
      return;
    }

    const filters = structuredClone(filtersContext.apiReadyFilters);
    filters.canonicalRegionSetIds = [props.canonicalRegionSetId];
    ecdContext.getTopCountiesByGapDrillInData(filters).then((response) => {
      const newData: TopCountiesFlyoutTableData[] = response.map((project) => {
        const projectLink = `/projects/${project.customProjectId}/adequacy/county/${props.countySSA}?all=false&gaps=true`
        /* eslint-disable sort-keys */
        return {
          [wafflesKeyColumnName]: project.customProjectId.toString(),
          projectName: <Link key={project.projectName} to={projectLink} target="_blank" rel="noopener noreferrer" className="qes-button qes-button-secondary">{project.projectName}</Link>,
          compositeFillableGaps: project.fillableGapCount.toLocaleString(),
          unfillableGaps: project.unfillableGapCount.toLocaleString(),
          overallGaps: project.gapCount.toLocaleString(),
        };
        /* eslint-enable sort-keys */
      });
      customSort(newData, "compositeFillableGaps", SortDirections.Desc);
    });
  }

  const customSort = (records: SharedEcdDrillInTableData[], column: keyof SharedEcdDrillInTableData, direction: SortDirections) => {
    const newData = sharedEcdDrillInCustomSort<SharedEcdDrillInTableData>(
      data.length > 0 ? data : records,
      column,
      direction,
      "compositeFillableGaps",
      SortDirections.Desc
    );

    setData(newData);
    return records;
  }

  const paginationProps: WafflesPaginationProps = useMemo(() => (
    sharedEcdDrillInPaginationProps(skip, take, setSkip, data, filterString)
  ), [data, skip, take, filterString]);

  const slicedData = useMemo(() => {
    return sharedEcdDrillInSlicedTableData(data, filterString, skip, take);
  }, [data, skip, take, filterString]);

  return (
    <Accordion
      key={props.countyName}
      title={props.countyName}
      onPanelWillOpen={onOpenAccordion}
      testId={`ecd-county-by-gaps-flyout__${props.countyName}-accordion`}
    >
      <SearchBox
        fieldsetClass="row mx-0"
        inputLabel="Filter on Project Name"
        onSubmit={(value) => setFilterString(value)}
        testId={`ecd-specialty-by-gaps-accordion__filter--${props.canonicalRegionSetId}`}
      />
      <Waffles<TopCountiesFlyoutTableData>
        columns={sharedEcdDrillInColumnConfig}
        customSort={customSort}
        data={slicedData}
        enablePagination={true}
        loading={data.length === 0 && filterString === ""}
        fullWidth={true}
        paginationProps={paginationProps}
        passThroughSorting={true}
        testId={`ecd-specialty-by-gaps-accordion__table--${props.canonicalRegionSetId}`}
      />
    </Accordion>
  )
}